import { displayAllowanceType } from "@Shared/Entities/Supplier/Response/Proposal/AllowanceTypeLabel";
import { AllowanceTypes } from "@Shared/Entities/Supplier/Response/Proposal/enums";
import { normalizeNetworkId } from "../AmendTariffs/types";
import { formatAllowanceValue, formatDataAllowance, formatDisplayDataAllowance, formatMoney, formatPlanLabel, getCommonCharges, getCustomerEmail, getCustomerName, getCustomerPhone, getCustomerService, getDealId, getLockedLines, getParameters, getRoaming, mapStatus, parseDealDate, } from "./chosenPackageHelpers";
const UK_CHARGES_ORDER = [
    AllowanceTypes["Biz rate Calls (mins)"],
    AllowanceTypes["Prem Calls"],
    AllowanceTypes["Std SMS"],
    AllowanceTypes["Prem SMS"],
    AllowanceTypes["UK IDD to EU (mins)"],
    AllowanceTypes["UK IDD to USA & Canada (mins)"],
    AllowanceTypes["UK IDD to APAC (mins)"],
    AllowanceTypes["UK IDD to ROW (mins)"],
];
const formatAllowanceLimit = (value) => {
    if (value === undefined || value === null || value === "") {
        return "Unlimited";
    }
    if (value === "Unlimited" || String(value).toLowerCase() === "unlimited") {
        return "Unlimited";
    }
    return String(value);
};
const formatPhonesSummary = (parameters) => {
    const fundingOption = parameters?.fundingOption ?? parameters?.deviceFundOptions ?? "none";
    if (fundingOption === "leasing") {
        return "Leasing";
    }
    if (fundingOption === "hardwareFund") {
        return "Fund";
    }
    return "None";
};
// SmallTimeFrame/Frequency enums are 0-indexed, lower is better: 0 = good, 1-2 = medium, 3+ = bad
const serviceLevel = (value) => {
    if (value === undefined) {
        return "medium";
    }
    if (value === 0) {
        return "good";
    }
    return value <= 2 ? "medium" : "bad";
};
const buildCustomerServiceBadges = (customerService) => {
    if (!customerService) {
        return [];
    }
    const badges = [
        {
            label: "Response Times",
            tone: serviceLevel(customerService.normalResolutionTime),
        },
        {
            label: "Bill Analysis",
            tone: serviceLevel(customerService.billAnalysisFrequency),
        },
    ];
    if (customerService.pacCodeCharge === false &&
        customerService.additionalTerminationPenalties === false) {
        badges.push({ label: "No Hidden Costs", tone: "good" });
    }
    else if (customerService.pacCodeCharge ||
        customerService.additionalTerminationPenalties) {
        badges.push({ label: "Hidden Costs", tone: "bad" });
    }
    return badges;
};
const buildRoamingType = (roaming) => {
    if (roaming?.eu?.isItRoamLikeAtHome) {
        return "Roam like at Home";
    }
    if (roaming?.eu?.isRoamingFree) {
        return "Roaming Allowance";
    }
    return "Roaming Allowance";
};
const buildRoamingCharges = (roaming) => {
    const rows = [];
    if (roaming?.eu) {
        rows.push({
            label: "EU Cost per Day",
            value: formatMoney(roaming.eu.dailyCharge ?? 0),
        });
    }
    const nonEuPackages = roaming?.nonEu ?? [];
    if (nonEuPackages.length > 0) {
        const charges = nonEuPackages.map((roamingPackage) => roamingPackage.dailyCharge ?? 0);
        const min = Math.min(...charges);
        const max = Math.max(...charges);
        rows.push({
            label: "Non-EU Cost per Day",
            value: min === max
                ? formatMoney(min)
                : `${formatMoney(min)}–${formatMoney(max)}`,
        });
    }
    return rows;
};
export const mapChosenPackageToDealDetail = (raw) => {
    const parameters = getParameters(raw);
    const networkId = normalizeNetworkId(String(parameters?.platform ?? ""));
    const id = getDealId(raw);
    if (!networkId || !id || !parameters) {
        return null;
    }
    const submittedAt = parseDealDate(raw);
    const contractMonths = Number(raw.contractLength ?? parameters.contractLength ?? 24);
    const lineCount = parameters.lines ?? 0;
    const monthlyLinesCost = Number(raw.monthlyLinesCostWithVAT ?? 0);
    const monthlyTotalCost = Number(raw.monthlyTotalCostWithVAT ?? 0);
    const monthlyDeviceCost = Math.max(0, monthlyTotalCost - monthlyLinesCost);
    const totalContractCost = Number(raw.totalContractCostWithVAT ?? 0);
    const perLineMonthly = lineCount > 0 ? monthlyLinesCost / lineCount : monthlyLinesCost;
    const lockedLines = getLockedLines(raw);
    const lineItems = [];
    if (lockedLines.length > 0) {
        const tierOrder = [];
        const tierMap = new Map();
        for (const line of lockedLines) {
            const dataValue = line.allowances?.data?.value;
            const lineCost = line.costWithVAT ?? 0;
            const key = `${lineCost}-${dataValue ?? ""}`;
            if (tierMap.has(key)) {
                tierMap.get(key).count++;
            }
            else {
                tierOrder.push(key);
                tierMap.set(key, {
                    count: 1,
                    cost: lineCost,
                    dataValue,
                });
            }
        }
        for (const key of tierOrder) {
            const tier = tierMap.get(key);
            const dataLabel = tier.dataValue === undefined
                ? ""
                : `(${formatAllowanceValue(tier.dataValue)} data) `;
            lineItems.push({
                count: tier.count,
                label: "Lines",
                rateLabel: `${dataLabel}× ${formatMoney(tier.cost)} per month`,
                total: formatMoney(tier.cost * tier.count),
            });
        }
    }
    else {
        lineItems.push({
            count: lineCount,
            label: "Lines",
            rateLabel: `× ${formatMoney(perLineMonthly)} per month`,
            total: formatMoney(monthlyLinesCost),
        });
    }
    if (monthlyDeviceCost > 0) {
        const perPhoneMonthly = lineCount > 0 ? monthlyDeviceCost / lineCount : monthlyDeviceCost;
        lineItems.push({
            count: lineCount,
            label: "Phones",
            rateLabel: `× ${formatMoney(perPhoneMonthly)} per month`,
            total: formatMoney(monthlyDeviceCost),
        });
    }
    const firstLine = lockedLines[0];
    const dataAllowance = formatDisplayDataAllowance(raw, parameters);
    const roaming = getRoaming(raw, parameters);
    const customerService = getCustomerService(raw, parameters);
    const commonCharges = getCommonCharges(raw, parameters);
    const customer = raw.customer;
    const registeredCompany = customer &&
        typeof customer === "object" &&
        typeof customer
            .isRegisteredCompany === "boolean"
        ? customer.isRegisteredCompany
        : typeof raw.isRegisteredCompany === "boolean"
            ? raw.isRegisteredCompany
            : false;
    const companyNumber = customer &&
        typeof customer === "object" &&
        typeof customer.companyNo === "string"
        ? customer.companyNo
        : typeof raw.companyNumber === "string"
            ? raw.companyNumber
            : "—";
    return {
        id,
        date: submittedAt,
        submittedAt,
        customerName: getCustomerName(raw),
        customerEmail: getCustomerEmail(raw),
        dealSummary: `${lineCount} ${lineCount === 1 ? "line" : "lines"} · ${formatDataAllowance(raw, parameters)} · ${formatPlanLabel(parameters)}`,
        networkId,
        status: mapStatus(raw.status ?? raw.dealStatus),
        phone: getCustomerPhone(raw),
        registeredCompany,
        companyNumber,
        contractMonths,
        lines: lineCount,
        dataAllowance,
        planType: formatPlanLabel(parameters),
        totalContractCost: formatMoney(totalContractCost),
        costBreakdown: {
            lineItems,
            totalPerMonth: formatMoney(monthlyTotalCost),
            totalForContract: formatMoney(totalContractCost),
            totalInclPhones: monthlyDeviceCost > 0
                ? formatMoney(monthlyDeviceCost * contractMonths)
                : "—",
        },
        quickStats: {
            lines: String(lineCount),
            calls: formatAllowanceLimit(firstLine?.allowances?.callLimit),
            texts: formatAllowanceLimit(firstLine?.allowances?.smsLimit),
            data: dataAllowance,
            phones: formatPhonesSummary(parameters),
            contract: `${contractMonths} Months`,
        },
        ukCharges: UK_CHARGES_ORDER.filter((type) => type in commonCharges).map((type) => ({
            label: displayAllowanceType(type),
            value: formatMoney(commonCharges[type]),
        })),
        roamingCharges: buildRoamingCharges(roaming),
        additionalServices: {
            customerServiceBadges: buildCustomerServiceBadges(customerService),
            roamingType: buildRoamingType(roaming),
            phones: formatPhonesSummary(parameters),
            simChange: typeof raw.simChange === "string" ? raw.simChange : "—",
        },
    };
};
